// @ds/actions/useProcesses — batch-process types, context object, hook.
//
// Split out of `ProcessContext.tsx` so that module exports components only.
import React, { createContext, useContext } from "react";

export type BatchItemStatus = "pending" | "processing" | "success" | "warning" | "error" | "skipped";

export interface BatchItem {
  id: string;
  label: string;
  status: BatchItemStatus;
  message?: string;
  /** For pipeline mode: which phase this item is currently in / failed at. */
  phase?: string;
  /** Server-side job this item waits on once the client-side work is done (e.g. the
   *  document_jobs id returned by an accepted upload). A "processing" item with a jobId is
   *  resolved by a watcher, not by the runner: it survives a page reload and does not hold the
   *  tab-close warning. */
  jobId?: string;
  /** When the item started waiting on `jobId` (epoch ms, set by `awaitJob`) — a watcher
   *  can tell rows read before it (an earlier run of a re-run job) from current ones. */
  waitingSince?: number;
}

export type ProcessStatus = "running" | "completed" | "completed_with_errors" | "stale";

export interface Process {
  id: string;
  type: string;
  label: string;
  description?: string;
  status: ProcessStatus;
  items: BatchItem[];
  /** Phase labels for pipeline mode display. */
  phases?: string[];
  createdAt: Date;
  completedAt?: Date;
  metadata?: Record<string, unknown>;
}

export interface ItemCallbacks {
  retry?: () => Promise<void>;
  fix?: () => void;
}

// ── Context type ────────────────────────────────────────────────────────

export interface ProcessContextType {
  processes: Process[];

  startProcess: (
    type: string, label: string, items: BatchItem[],
    metadata?: Record<string, unknown>, description?: string, phases?: string[],
  ) => string;
  updateItem: (processId: string, itemId: string, status: BatchItemStatus, message?: string, phase?: string) => void;
  completeProcess: (processId: string) => void;
  /** Server-side counterpart of `completeProcess`: completes the process exactly like
   *  `completeProcess` iff it is still "running" and no item is pending/processing (i.e.
   *  nothing is waiting on a job). Otherwise it is a no-op — safe for a poll/realtime
   *  watcher to call repeatedly without causing extra renders. */
  settleProcess: (processId: string) => void;
  /** Moves an accepted item to wait on a server-side job: status becomes "processing",
   *  `jobId` is set, `message` is shown until a watcher resolves the job. */
  awaitJob: (processId: string, itemId: string, jobId: string, message?: string) => void;
  /** Re-opens a process as "running" with no completion time — e.g. right before a
   *  retried job starts waiting again. */
  reopenProcess: (processId: string) => void;
  removeProcess: (id: string) => void;
  clearCompleted: () => void;

  getProcess: (id: string) => Process | undefined;
  isRunning: (type: string, metadata?: Record<string, unknown>) => boolean;

  registerCallbacks: (processId: string, callbacks: Map<string, ItemCallbacks>) => void;
  getCallbacks: (processId: string, itemId: string) => ItemCallbacks | undefined;

  fixModal: React.ReactNode;
  setFixModal: (modal: React.ReactNode) => void;
}

export const ProcessContext = createContext<ProcessContextType | undefined>(undefined);

export const useProcesses = () => {
  const ctx = useContext(ProcessContext);
  if (!ctx) throw new Error("useProcesses must be used within ProcessProvider");
  return ctx;
};
